import React, { useState, useEffect, Suspense, useCallback } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import theme from "assets/theme";
import Sidenav from "examples/Sidenav";
import EmployeeSidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import brand from "assets/images/datagonomix.jpg";
import routes from "routes";
import employeeRoutes from "employeeRoutes";
import HRRoutes from "Pages/AdministratorDashboard/HRRoutesLink";
import EmployeeRoutes from "Pages/EmployeeDashboard/EmployeeRoutesLink";
import SignIn from "./Pages/AdministratorDashboard/authentication/sign-in";
import Dashboard from "./Pages/AdministratorDashboard/dashboard";
import EmpDashboard from "./Pages/EmployeeDashboard/dashboard";
import { useSoftUIController } from "context";
import { message } from "antd";
import ProtectedRoute from "Pages/AdministratorDashboard/authentication/ProtectedHRFile/ProtectedHRFile";
import ViewCareer from "./Pages/AdministratorDashboard/View Career Page/ViewCareerPage";
import EmployeeSignIn from "./Pages/EmployeeDashboard/authentication/sign-in/signIn";
import HiringOnboarding from "./Pages/AdministratorDashboard/Hiring/HiringOnboarding/HiringOnboarding";
import AnnouncementDetail from "./Pages/AdministratorDashboard/dashboard/components/Announcement/AnnuncementDetail";
import ShortListEmployeeForm from "./Pages/EmployeeDashboard/ShortListEmployeeForm/ShortListEmployeeForm";
import SetPassword from "./Pages/SetPassword";
import JobOpenings from "Pages/AdministratorDashboard/View Career Page/JobOpenings";
import Loader from "./Common/Loader/Loader";
import ForgotPassword from "./Pages/AdministratorDashboard/authentication/forgotPassword";
import CheckEmail from "./Pages/AdministratorDashboard/authentication/forgotPassword/CheckEmail";
import ResetPassword from "./Pages/AdministratorDashboard/authentication/forgotPassword/ResetPassword";
import Register from "./Pages/EmployeeDashboard/authentication/EmplRegister";
import ApplicantPortal from "./Pages/EmployeeDashboard/ShortListEmployeeForm/EmployeeJobpostings";
import CandidateCheckEmail from "./Pages/EmployeeDashboard/authentication/CandidateChkEmail";
import CandidateSetPassword from "./Pages/EmployeeDashboard/authentication/CandidateSetPassword";
import CandidateForgotPasswrod from "./Pages/EmployeeDashboard/authentication/sign-in/CandidateForgotPassword";
import CandidateForgotCheckEmail from "./Pages/EmployeeDashboard/authentication/sign-in/CandidateFogotChkEmail";
import CandidateResetPassword from "./Pages/EmployeeDashboard/authentication/sign-in/CandidateResetPassword";
import ViewFeedback from "./Pages/AdministratorDashboard/Hiring/ViewFeedback";

export default function App() {
  const [controller] = useSoftUIController();
  const { layout, sidenavColor } = controller;
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect immediately after login (token + role) - FIXED
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    const token = localStorage.getItem("token");
    const currentPath = location.pathname;

    // Only redirect if we're on signIn page and have credentials
    if (token && storedRole && currentPath === "/signIn") {
      let redirectPath = "/dashboard"; // default
      switch (storedRole) {
        case "Admin":
          redirectPath = "/dashboard";
          break;
        case "Employee":
          redirectPath = "/employeedashboard";
          break;

        default:
          console.warn("Unknown role for redirect:", storedRole);
          return;
      }

      navigate(redirectPath, { replace: true });
    }
  }, [navigate, location.pathname]); // Only depend on pathname, not entire location

  // Route generator (keeps ProtectedRoute usage consistent)
  const getRoutes = useCallback(
    (allRoutes) =>
      allRoutes.map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          let element = null;

          // Admin-protected routes
          if (
            route.route === "/dashboard" ||
            route.route === "/attendance" ||
            route.route === "/document-store" ||
            route.route === "/timeoff" ||
            route.route === "/attendance-report" ||
            route.route === "/hiring" ||
            route.route === "/onboarding" ||
            route.route === "/report" ||
            route.route === "/received-Documents" ||
            route.route === "/performance" ||
            route.route === "/training" ||
            route.route === "/assets" ||
            route.route === "/complaints" ||
            route.route === "/compliance" ||
            route.route === "/organization/designations" ||
            route.route === "/organization/departments" ||
            route.route === "/organization/job-titles" ||
            route.route === "/organization/directory" ||
            route.route === "/organization/hierarchy" ||
            route.route === "/user-invitation"
          ) {
            element = <ProtectedRoute element={route.component} />;
          }

          // Employee-protected routes
          else if (
            route.route.startsWith("/employeedashboard") ||
            route.route === "/employeedashboard/personal" ||
            route.route === "/employeedashboard/jobemployee" ||
            route.route === "/employeedashboard/attendance" ||
            route.route === "/employeedashboard/leave-request" ||
            route.route === "/employeedashboard/documents" ||
            route.route === "/employeedashboard/emergency" ||
            route.route === "/employeedashboard/complaints" ||
            route.route === "/employeedashboard/performance-review" ||
            route.route === "/employeedashboard/performance-review/report" ||
            route.route === "/employeedashboard/explore-careers"
          ) {
            element = <ProtectedRoute element={route.component} />;
          }

          if (element) {
            return <Route exact path={route.route} element={element} key={route.key} />;
          }
        }

        return null;
      }),
    []
  );

  // Sidebar visibility (includes signIn)
  const showSidebar = ![
    "/signIn",
    "/forgot-password",
    "/check-email",
    "/reset-password",
    "/viewcareer",
    "/empsignIn",
    "/applicantPortal",
    "/empRegister",
    "/candidate-check-email",
    "/candidate-set-password",
    "/candidate-forgot-password",
    "/candidate-forgot-chekemail",
    "/candidate-reset-password",
    "/set-password",
    /^\/Employee-Onboarding\/[^/]+\/[^/]+$/,
    /^\/viewcandidate\/[^/]+$/,
    /^\/hiringOnboarding\/[^/]+$/,
    
    /^\/jobOpenings\/[^/]+$/,
    /^\/announcement-detail\/[^/]+$/,
    "/shortlistEmployeeform",
  ].some((pattern) => {
    if (typeof pattern === "string") {
      return location.pathname === pattern;
    } else if (pattern instanceof RegExp) {
      return pattern.test(location.pathname);
    }
    return false;
  });

  const isEmployeeDashboard = location.pathname.startsWith("/employeedashboard");

  // FIXED: Single Authorization Check - PREVENT INFINITE LOOP
  useEffect(() => {
    const checkRoleAccess = () => {
      const storedRole = localStorage.getItem("role");
      const token = localStorage.getItem("token");
      const currentPath = location.pathname;

      // Update role state if changed
      if (storedRole !== role) {
        setRole(storedRole);
      }

      // Define public routes that don't require authentication
      const publicRoutes = [
        "/signIn",
        "/forgot-password",
        "/reset-password",
        "/applicantPortal",
        "/check-email",
        "/viewcareer",
        "/empsignIn",
        "/empRegister",
        "/candidate-check-email",
        "/candidate-set-password",
        "/candidate-forgot-password",
        "/candidate-forgot-chekemail",
        "/candidate-reset-password",
        "/set-password",
        // "/Employee-Onboarding",
        "/hiringOnboarding",
        "/viewcandidate",
        "/jobOpenings",
        "/announcement-detail",
        "/shortlistEmployeeform",
      ];

      // Check if current path is a public route
      const isPublicRoute = publicRoutes.some((route) => {
        if (typeof route === "string") {
          return currentPath === route || currentPath.startsWith(route);
        } else if (route instanceof RegExp) {
          return route.test(currentPath);
        }
        return false;
      });

      // If this is a public route, don't check authentication
      if (isPublicRoute) {
        return;
      }

      // If no token or role, redirect to signIn (unless already there)
      if (!token || !storedRole) {
        if (currentPath !== "/signIn") {
          navigate("/signIn", { replace: true });
        }
        return;
      }

      // If we have token and role, check route authorization
      const isAdminRoute =
        currentPath.startsWith("/dashboard") ||
        [
          "/employees",
          "/hiring",
          "/report",
          "/performance",
          "/training",
          "/assets",
          "/complaints",
          "/compliance",
        ].includes(currentPath);

      const isEmployeeRoute = currentPath.startsWith("/employeedashboard");
      const unauthorized =
        (storedRole === "Employee" && isAdminRoute) || (storedRole === "Admin" && isEmployeeRoute);
      if (unauthorized) {
        message.error("Unauthorized access! Please log in again.");
        localStorage.clear(); // Clear invalid credentials
        navigate("/signIn", { replace: true });
      }
    };
    // Add a small delay to prevent immediate re-triggering
    const timeoutId = setTimeout(checkRoleAccess, 100);
    return () => clearTimeout(timeoutId);
  }, [location.pathname, navigate, role]);
  // FIXED: Sync role changes across tabs - REMOVED INTERVAL
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "role" || e.key === "token") {
        const storedRole = localStorage.getItem("role");
        setRole(storedRole);
        // Only redirect if token was removed and not on a public route
        if (
          !storedRole &&
          location.pathname !== "/signIn" &&
          !location.pathname.startsWith("/viewcareer")
        ) {
          navigate("/signIn", { replace: true });
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
   return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [navigate, location.pathname]);

  // Build top-level accessible routes depending on role
  const getAccessibleRoutes = useCallback(() => {
    if (!role) {
      return <Route path="*" element={<SignIn />} />;
    }

    if (role === "Admin") {
      return (
        <>
          <Route path="/dashboard" element={<Dashboard />} />
          {HRRoutes()}
          {getRoutes(routes)}
          <Route path="*" element={<Dashboard />} />
        </>
      );
    }

    if (role === "Employee") {
      return (
        <>
          <Route path="/employeedashboard" element={<EmpDashboard />} />
          {EmployeeRoutes()}
          {getRoutes(employeeRoutes)}
          <Route path="*" element={<EmpDashboard />} />
        </>
      );
    }

    return <Route path="*" element={<SignIn />} />;
  }, [role, getRoutes]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Loader loading={loading} />

      {/* Fixed Sidebar Logic */}
      {role && layout === "dashboard" && showSidebar && (
        <>
          {role === "Employee" && isEmployeeDashboard ? (
            <EmployeeSidenav
              color={sidenavColor}
              brand={brand}
              brandName="Employee Dashboard"
              routes={employeeRoutes}
            />
          ) : role === "Admin" ? (
            <Sidenav 
            color={sidenavColor} 
            brand={brand}
             brandName="VelorHR Admin"
              routes={routes} />
          ) : null}
          <Configurator />
        </>
      )}

      <Suspense fallback={<Loader loading={true} />}>
        <Routes>
          <Route path="/signIn" element={<SignIn />} />
          <Route path="/forgot-password" element={< ForgotPassword/>} />
          <Route path = "/check-email" element = { < CheckEmail /> } />
          <Route path = "/reset-password" element = { < ResetPassword/> } />
          <Route path="/viewcareer" element={<ViewCareer />} />
          <Route
            path="/jobOpenings/:jobPostingId"
            name="JobOpenings"
            element={<JobOpenings />}
          />{" "}
          <Route path="/empsignIn" name="Sign In" element={<EmployeeSignIn />} />
          <Route path="/empRegister" name = "Register" element= {<Register/>} />
          <Route path="/candidate-check-email" name = "Register Success" element= {<CandidateCheckEmail/>} />
          <Route path="/candidate-set-password" name = "Candidate Set Password" element = {< CandidateSetPassword/>} />
          <Route path= "/candidate-forgot-password"  name = "Candidate Forgot Password" element = {< CandidateForgotPasswrod />} />
          <Route path= "/candidate-forgot-chekemail"  name = "Candidate Forgot Password" element = {< CandidateForgotCheckEmail />} />
          <Route path= "/candidate-reset-password" name =" Candidate Reset Password" element ={< CandidateResetPassword />} />
          <Route path="/set-password" name="set-password" element={<SetPassword />} />
          <Route path="/hiringOnboarding/:param1/:param2" element={<HiringOnboarding />} />
          <Route path="/hiringOnboarding/:param1" element={<HiringOnboarding />} />
          <Route path="/announcement-detail/:id" element={<AnnouncementDetail />} />
          <Route path="/shortlistEmployeeform" element={<ShortListEmployeeForm />} />
          <Route path="/applicantPortal" element={< ApplicantPortal/>} />
          <Route path="/view-feedback/:jobApplicantId" element={<ViewFeedback />} />
          {getAccessibleRoutes()}
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}




